/**
 * Lifecycle Recon API
 * Fetches lifecycle profiles, assessment history, and lifecycle-specific tool lists
 * from the manage_lifecycle_profile Azure Function.
 */

import { apiFetch } from '../../../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/**
 * Fetch tools for a site with joined lifecycle profile + part-number counts.
 * Calls GET /manage_lifecycle_profile?action=list&site_id=...&caller_email=...
 * Returns flat objects: all Tool fields + lifecycle fields + pn_count.
 */
export async function getLifecycleTools(siteId, callerEmail) {
  const params = new URLSearchParams({
    action: 'list',
    site_id: siteId,
  });
  if (callerEmail) params.append('caller_email', callerEmail);

  const response = await apiFetch(`${API_BASE_URL}/manage_lifecycle_profile?${params}`);
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  return response.json();
}

/**
 * Canonical multi-container search (Tools + AssetTags + PartNumbers) for a
 * site, used when the query meets the minimum length. Mirrors the S4/S5
 * reference (inventoryApi.searchInventoryTools). Returns raw snake_case tool
 * docs; asset-tag / part-number hits carry `_match_context`. Shorter queries
 * fall back to getLifecycleTools.
 * GET /search_inventory?site_id={siteId}&q={query}&caller_email={email}
 */
export async function searchLifecycleTools(siteId, query, callerEmail) {
  const params = new URLSearchParams({ site_id: siteId, q: query });
  if (callerEmail) params.append('caller_email', callerEmail);

  const response = await apiFetch(`${API_BASE_URL}/search_inventory?${params}`);
  if (!response.ok) throw new Error(`Search failed: ${response.status}`);

  return response.json();
}

/**
 * Fetch the lifecycle profile for a single tool.
 * Calls GET /manage_lifecycle_profile?tool_id=...&caller_email=...
 * Returns the profile document or null if 404.
 */
export async function getLifecycleProfile(toolId, callerEmail) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);

  const response = await apiFetch(`${API_BASE_URL}/manage_lifecycle_profile?${params}`);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  return response.json();
}

/**
 * Update (PATCH) fields on a lifecycle profile.
 * Calls PATCH /manage_lifecycle_profile?tool_id=...&caller_email=...
 * Auto-creates the profile if it doesn't exist yet.
 * project_id is required — included in the JSON body.
 */
export async function updateLifecycleProfile(toolId, fields, callerEmail, projectId) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);

  const body = { ...fields };
  if (projectId) body.project_id = projectId;

  const response = await apiFetch(`${API_BASE_URL}/manage_lifecycle_profile?${params}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Save failed (${response.status})`);
  }

  return response.json();
}

/**
 * Fetch assessment history for a tool (up to 10 most recent).
 * Calls GET /manage_lifecycle_profile?action=assessments&tool_id=...&caller_email=...
 */
export async function getAssessmentHistory(toolId, callerEmail) {
  const params = new URLSearchParams({
    action: 'assessments',
    tool_id: toolId,
  });
  if (callerEmail) params.append('caller_email', callerEmail);

  const response = await apiFetch(`${API_BASE_URL}/manage_lifecycle_profile?${params}`);
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  return response.json();
}

/**
 * SCR Document — generate upload SAS URL.
 * Returns { upload_url, blob_name }
 */
export async function getScrDocumentUploadUrl(toolId, lifecycleId, originalFilename, fileSizeBytes, callerEmail) {
  const response = await apiFetch(`${API_BASE_URL}/manage_scr_document`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tool_id: toolId,
      lifecycle_id: lifecycleId,
      original_filename: originalFilename,
      file_size_bytes: fileSizeBytes,
      caller_email: callerEmail,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Upload URL failed (${response.status})`);
  }
  return response.json();
}

/**
 * SCR Document — generate download SAS URL.
 * Returns { download_url }
 */
export async function getScrDocumentDownloadUrl(blobName, callerEmail) {
  const params = new URLSearchParams({ blob_name: blobName, caller_email: callerEmail });
  const response = await apiFetch(`${API_BASE_URL}/manage_scr_document?${params}`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Download URL failed (${response.status})`);
  }
  return response.json();
}

/**
 * SCR Document — delete blob + remove from LifecycleProfile.
 * Returns { scr_documents: [...] }
 */
export async function deleteScrDocument(toolId, lifecycleId, blobName, callerEmail) {
  const response = await apiFetch(`${API_BASE_URL}/manage_scr_document`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tool_id: toolId,
      lifecycle_id: lifecycleId,
      blob_name: blobName,
      caller_email: callerEmail,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Delete failed (${response.status})`);
  }
  return response.json();
}
